import { CandidatoService } from './candidato.service';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useEffect, useMemo, useState } from 'react';

type ArquivoAusenteRow = {
  id: string;
  filename: string;
  candidatoId: number | null;
  candidatoNome: string | null;
  caminhoServidor?: string | null;
};

type ChecagemFile = {
  id: string;
  filename: string;
  caminhoServidor: string | null;
  candidatoId: number | null;
  candidatoNome: string | null;
};

type ErroOperacaoRow = {
  id: string;
  mensagem: string;
};

export default function CandidatoGestaoArquivosPage() {
  const { setActions } = useBreadcrumbActions();
  const params = useParams();
  const parsedId = params.id != null ? Number(params.id) : undefined;
  const processoSeletivoId = parsedId != null && Number.isFinite(parsedId) ? parsedId : undefined;

  const candidatoService = useMemo(() => new CandidatoService(), []);
  const [vinculandoLegado, setVinculandoLegado] = useState(false);
  const [checando, setChecando] = useState(false);
  const [checados, setChecados] = useState(0);
  const [totalChecagem, setTotalChecagem] = useState(0);
  const [arquivoAtual, setArquivoAtual] = useState<string | null>(null);
  const [ausentes, setAusentes] = useState<ArquivoAusenteRow[]>([]);
  const [presentes, setPresentes] = useState<ArquivoAusenteRow[]>([]);
  const [filtroCandidato, setFiltroCandidato] = useState<string>('');
  const [errosAdministrativos, setErrosAdministrativos] = useState<string[]>([]);
  const tableStateAusentes = useDataTableState('candidato-arquivos-ausentes');
  const tableStatePresentes = useDataTableState('candidato-arquivos-presentes');
  const tableStateErros = useDataTableState('candidato-arquivos-erros-admin');

  useEffect(() => {
    setActions({});
    return () => setActions({});
  }, [setActions]);

  const handleVincularArquivosLegados = async () => {
    const escopo =
      processoSeletivoId != null
        ? ' os candidatos deste processo seletivo'
        : ' todos os candidatos do sistema';
    if (
      !window.confirm(
        `Atualizar candidato_id em candidato_arquivo com base nos UUIDs nas colunas legadas (*_url) do candidato, para${escopo}? ` +
        'Registros já corretos serão ignorados. Valores que não forem UUID serão contados como formato inválido.',
      )
    ) {
      return;
    }
    setVinculandoLegado(true);
    setErrosAdministrativos([]);
    try {
      const r = await candidatoService.vincularArquivosLegados(processoSeletivoId);
      setErrosAdministrativos(r.erros);
      const resumo = `${r.vinculados} vínculo(s) atualizado(s), ${r.jaCorretos} já corretos, ${r.formatoInvalido} valor(es) não-UUID ignorado(s).`;
      if (r.erros.length > 0) {
        toast.warning(`${resumo} ${r.erros.length} problema(s) — veja o card abaixo.`);
      } else {
        toast.success(resumo);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao vincular arquivos legados.');
    } finally {
      setVinculandoLegado(false);
    }
  };

  const handleChecarArquivos = async () => {
    if (processoSeletivoId == null) {
      toast.error('Processo seletivo não identificado na URL; não é possível checar arquivos.');
      return;
    }

    setChecando(true);
    setChecados(0);
    setTotalChecagem(0);
    setArquivoAtual(null);
    setAusentes([]);
    setPresentes([]);
    setFiltroCandidato('');

    try {
      const files = (await candidatoService.listarArquivosParaChecagem(processoSeletivoId)) as unknown as ChecagemFile[];
      setTotalChecagem(files.length);

      const missing: ArquivoAusenteRow[] = [];
      const found: ArquivoAusenteRow[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setArquivoAtual(f.filename);

        let exists = false;
        let caminhoServidorResolved: string | null = f.caminhoServidor ?? null;
        try {
          // Checagem física preferencial por ID (também resolve e atualiza caminhoServidor no backend)
          const r = await candidatoService.arquivoExisteNoServidorPorId(f.id);
          exists = r.exists;
          caminhoServidorResolved = (r.caminhoServidor ?? f.caminhoServidor ?? null) as string | null;
        } catch {
          // fallback: se der erro, tenta pelo caminho (quando existir)
          try {
            if (f.caminhoServidor) {
              exists = await candidatoService.arquivoExisteNoServidor(f.caminhoServidor);
              if (exists) {
                // Usa o caminho que foi validado fisicamente no fallback.
                caminhoServidorResolved = (f.caminhoServidor ?? '').trim().replace(/\\/g, '/');
              }
            } else {
              exists = false;
            }
          } catch {
            exists = false;
          }
        }

        const candidatoIdNum = f.candidatoId != null ? Number(f.candidatoId) : null;
        const candidatoId = candidatoIdNum != null && Number.isFinite(candidatoIdNum) ? candidatoIdNum : null;
        const candidatoNome = f.candidatoNome ?? null;

        if (!exists) {
          missing.push({
            id: f.id,
            filename: f.filename,
            candidatoId,
            candidatoNome,
            caminhoServidor: f.caminhoServidor ?? null,
          });
        } else {
          found.push({
            id: f.id,
            filename: f.filename,
            candidatoId,
            candidatoNome,
            caminhoServidor: caminhoServidorResolved ?? null,
          });
        }

        setChecados(i + 1);
      }

      setAusentes(missing);
      setPresentes(found);

      if (missing.length === 0) {
        toast.success('Checagem concluída. Nenhum arquivo ausente no servidor.');
      } else {
        toast.warning(`Checagem concluída. ${missing.length} arquivo(s) ausente(s) no servidor.`);
      }
    } catch (error: any) {
      toast.error(error?.message || 'Erro ao checar arquivos.');
    } finally {
      setArquivoAtual(null);
      setChecando(false);
    }
  };

  const normalizarTexto = (valor: string): string => {
    return valor
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .trim()
      .toLowerCase();
  };

  const filtroCandidatoTrim = filtroCandidato.trim();
  const filtroCandidatoNorm = normalizarTexto(filtroCandidatoTrim);
  const filtroCandidatoIdNum = /^\d+$/.test(filtroCandidatoTrim) ? Number(filtroCandidatoTrim) : null;

  const ausentesFiltrados = useMemo(() => {
    if (!filtroCandidatoTrim) return ausentes;

    return ausentes.filter((row) => {
      if (filtroCandidatoIdNum != null && row.candidatoId === filtroCandidatoIdNum) return true;

      const nome = normalizarTexto(row.candidatoNome ?? '');
      const idComoTexto = row.candidatoId != null ? String(row.candidatoId) : '';

      return nome.includes(filtroCandidatoNorm) || idComoTexto.includes(filtroCandidatoTrim);
    });
  }, [ausentes, filtroCandidatoIdNum, filtroCandidatoNorm, filtroCandidatoTrim]);

  const presentesFiltrados = useMemo(() => {
    if (!filtroCandidatoTrim) return presentes;

    return presentes.filter((row) => {
      if (filtroCandidatoIdNum != null && row.candidatoId === filtroCandidatoIdNum) return true;

      const nome = normalizarTexto(row.candidatoNome ?? '');
      const idComoTexto = row.candidatoId != null ? String(row.candidatoId) : '';

      return nome.includes(filtroCandidatoNorm) || idComoTexto.includes(filtroCandidatoTrim);
    });
  }, [presentes, filtroCandidatoIdNum, filtroCandidatoNorm, filtroCandidatoTrim]);

  const errosOperacaoRows: ErroOperacaoRow[] = useMemo(
    () => errosAdministrativos.map((mensagem, i) => ({ id: `erro-op-${i}`, mensagem })),
    [errosAdministrativos],
  );

  const errosOperacaoFiltrados = useMemo(() => {
    if (!filtroCandidatoTrim) return errosOperacaoRows;
    return errosOperacaoRows.filter((row) => {
      const m = normalizarTexto(row.mensagem);
      return m.includes(filtroCandidatoNorm) || row.mensagem.includes(filtroCandidatoTrim);
    });
  }, [errosOperacaoRows, filtroCandidatoNorm, filtroCandidatoTrim]);

  useEffect(() => {
    if (checando) return;
    tableStateAusentes.setCurrentPage(1);
    tableStatePresentes.setCurrentPage(1);
    tableStateErros.setCurrentPage(1);
  }, [filtroCandidato]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewFile = async (fileId: string) => {
    try {
      const blob = await candidatoService.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, '_blank');
      // Libera a URL criada para o blob. Mantemos um curto delay para não interferir na abertura da nova aba.
      if (!newWindow) {
        window.URL.revokeObjectURL(url);
      } else {
        window.setTimeout(() => window.URL.revokeObjectURL(url), 10000);
      }
    } catch {
      toast.error('Erro ao abrir arquivo.');
    }
  };

  const percent = totalChecagem > 0 ? Math.round((checados / totalChecagem) * 100) : 0;

  const colunasAusentes: Column<ArquivoAusenteRow>[] = [
    {
      key: 'candidatoNome',
      header: 'Candidato',
      render: (row) =>
        row.candidatoId != null && Number.isFinite(row.candidatoId) ? (
          <Link className="link-secondary" to={`/candidato/${row.candidatoId}`}>
            {row.candidatoNome || `#${row.candidatoId}`}
          </Link>
        ) : (
          '-'
        ),
    },
    { key: 'filename', header: 'Arquivo ausente' },
    {
      key: 'caminhoServidor',
      header: 'Caminho no servidor',
      invisible: true,
      render: (row) => row.caminhoServidor ?? '-',
    },
    {
      key: 'visualizar',
      header: 'Visualizar',
      render: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => handleViewFile(row.id)}
          disabled={checando || vinculandoLegado}
          title="Arquivo ausente fisicamente no servidor."
        >
          Visualizar
        </button>
      ),
    },
  ];

  const colunasPresentes: Column<ArquivoAusenteRow>[] = [
    {
      key: 'candidatoNome',
      header: 'Candidato',
      render: (row) =>
        row.candidatoId != null && Number.isFinite(row.candidatoId) ? (
          <Link className="link-secondary" to={`/candidato/${row.candidatoId}`}>
            {row.candidatoNome || `#${row.candidatoId}`}
          </Link>
        ) : (
          '-'
        ),
    },
    { key: 'filename', header: 'Arquivo presente' },
    {
      key: 'caminhoServidor',
      header: 'Caminho no servidor',
      invisible: true,
      render: (row) => row.caminhoServidor ?? '-',
    },
    {
      key: 'visualizar',
      header: 'Visualizar',
      render: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => handleViewFile(row.id)}
          disabled={checando || vinculandoLegado}
        >
          Visualizar
        </button>
      ),
    },
  ];

  const colunasErrosOperacao: Column<ErroOperacaoRow>[] = [
    {
      key: 'mensagem',
      header: 'Mensagem',
      render: (row) => <span className="text-break">{row.mensagem}</span>,
    },
  ];

  return (
    <div className="row">
      <div className="col-md-8">
        <h3>Gestão de Arquivos</h3>
        <span className="blockquote-footer pb-4 text-end">
          Ações administrativas para arquivos de candidatos.
        </span>
        {!checando && (
          <div className="text-muted small">
            Ausentes: <strong>{ausentes.length}</strong> | Presentes: <strong>{presentes.length}</strong>
            {errosAdministrativos.length > 0 ? (
              <>
                {' '}
                | Erros (último vínculo legado): <strong>{errosAdministrativos.length}</strong>
              </>
            ) : null}
          </div>
        )}
      </div>
      <div className="col-md-4">
        <br />
        <div className="float-end d-flex flex-wrap gap-2 justify-content-end">
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={handleVincularArquivosLegados}
            disabled={checando || vinculandoLegado}
            title="Preenche candidato_id em candidato_arquivo a partir dos UUIDs nas colunas *_url da tabela candidato (dados legados)."
          >
            {vinculandoLegado ? 'Vinculando...' : 'Vincular arquivos legados'}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleChecarArquivos}
            disabled={checando || vinculandoLegado}
          >
            {checando ? 'Checando...' : 'Checar arquivos'}
          </button>
        </div>
      </div>
      <hr className="pb-4" />

      {checando && (
        <div className="col-12 mb-3">
          <div className="d-flex justify-content-between">
            <div className="text-muted small">
              Progresso: {checados}/{totalChecagem} ({percent}%)
            </div>
            <div className="text-muted small">
              {arquivoAtual ? `Arquivo atual: ${arquivoAtual}` : ''}
            </div>
          </div>
          <div className="progress">
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${percent}%` }}
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
      )}

      {!checando && (
        <div className="col-12">
          <div className="d-flex justify-content-end mb-3">
            <div className="w-25">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Filtrar por candidato (nome ou ID)..."
                value={filtroCandidato}
                onChange={(e) => setFiltroCandidato(e.target.value)}
              />
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="fw-bold">Ausentes ({ausentes.length})</div>
              <button
                className="btn btn-link text-decoration-none p-0"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapse-arquivos-ausentes"
                aria-expanded={ausentes.length > 0}
                aria-controls="collapse-arquivos-ausentes"
              >
                {ausentes.length > 0 ? 'Recolher' : 'Expandir'}
              </button>
            </div>
            <div id="collapse-arquivos-ausentes" className={`collapse ${ausentes.length > 0 ? 'show' : ''}`}>
              <div className="card-body">
                {ausentes.length > 0 ? (
                  <DataTable
                    columns={colunasAusentes}
                    data={ausentesFiltrados}
                    initialSortKey="candidatoNome"
                    serverSide={false}
                    states={tableStateAusentes}
                    disableExportButtons={true}
                    showFilter={false}
                    disableColumnsFilter={true}
                  />
                ) : (
                  <div className="text-muted">Nenhum arquivo ausente no servidor.</div>
                )}
              </div>
            </div>
          </div>

          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div className="fw-bold">Presentes ({presentes.length})</div>
              <button
                className="btn btn-link text-decoration-none p-0"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#collapse-arquivos-presentes"
                aria-expanded={false}
                aria-controls="collapse-arquivos-presentes"
              >
                Expandir
              </button>
            </div>
            <div id="collapse-arquivos-presentes" className="collapse">
              <div className="card-body">
                {presentes.length > 0 ? (
                  <DataTable
                    columns={colunasPresentes}
                    data={presentesFiltrados}
                    initialSortKey="candidatoNome"
                    serverSide={false}
                    states={tableStatePresentes}
                    disableExportButtons={true}
                    showFilter={false}
                    disableColumnsFilter={true}
                  />
                ) : (
                  <div className="text-muted">Nenhum arquivo presente no servidor.</div>
                )}
              </div>
            </div>
          </div>

          <div className="card mb-3 border-warning">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="fw-bold">
                Erros — vínculo legado ({errosAdministrativos.length})
              </div>
              <div className="d-flex align-items-center gap-2">
                {errosAdministrativos.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setErrosAdministrativos([])}
                  >
                    Limpar lista
                  </button>
                ) : null}
                <button
                  className="btn btn-link text-decoration-none p-0"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapse-arquivos-erros-admin"
                  aria-expanded={errosAdministrativos.length > 0}
                  aria-controls="collapse-arquivos-erros-admin"
                >
                  {errosAdministrativos.length > 0 ? 'Recolher' : 'Expandir'}
                </button>
              </div>
            </div>
            <div
              id="collapse-arquivos-erros-admin"
              className={`collapse ${errosAdministrativos.length > 0 ? 'show' : ''}`}
            >
              <div className="card-body">
                {errosAdministrativos.length > 0 ? (
                  <DataTable
                    columns={colunasErrosOperacao}
                    data={errosOperacaoFiltrados}
                    initialSortKey="mensagem"
                    serverSide={false}
                    states={tableStateErros}
                    disableExportButtons={true}
                    showFilter={false}
                    disableColumnsFilter={true}
                  />
                ) : (
                  <div className="text-muted">
                    Nenhum erro registrado. Ao executar &quot;Vincular arquivos legados&quot;, falhas aparecem aqui em vez do toast.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

